/**
 * Resolve annotation id from POST /annotations (or similar) response bodies.
 * Backend shapes vary: top-level annotation_id, data.id, nested data.annotation.id, etc.
 */
export function normalizeAnnotationId(value: unknown): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function readIdFromRecord(record: Record<string, unknown> | null | undefined): number | undefined {
  if (!record) {
    return undefined;
  }
  return (
    normalizeAnnotationId(record.annotation_id) ??
    normalizeAnnotationId(record.id) ??
    normalizeAnnotationId((record.annotation as Record<string, unknown> | undefined)?.id) ??
    normalizeAnnotationId(
      (record.annotation as Record<string, unknown> | undefined)?.annotation_id
    )
  );
}

export function extractAnnotationIdFromSaveResponse(body: unknown): number | undefined {
  if (body == null || typeof body !== 'object') {
    return undefined;
  }

  const root = body as Record<string, unknown>;
  const fromRoot = readIdFromRecord(root);
  if (fromRoot) {
    return fromRoot;
  }

  const data = root.data;
  if (data == null) {
    return undefined;
  }

  if (typeof data === 'number' || typeof data === 'string') {
    return normalizeAnnotationId(data);
  }

  if (Array.isArray(data)) {
    for (const item of data) {
      if (item && typeof item === 'object') {
        const id = readIdFromRecord(item as Record<string, unknown>);
        if (id) {
          return id;
        }
      }
    }
    return undefined;
  }

  if (typeof data === 'object') {
    return readIdFromRecord(data as Record<string, unknown>);
  }

  return undefined;
}

/**
 * After first save, GET /annotations/study/{uid} and return the current grader's annotation id.
 */
export async function fetchAnnotationIdForGrader(
  studyUid: string,
  graderId: number | string,
  jobId?: number
): Promise<number | undefined> {
  try {
    const { default: ApiService } = await import('../services/ApiService');
    const apiService = ApiService.getInstance();
    const baseUrl = apiService.getGradingServiceUrl();
    const endpoint = `/annotations/study/${encodeURIComponent(studyUid)}`;
    const queryParams: Record<string, string> = {};
    if (jobId != null && jobId !== 0) {
      queryParams.job_id = String(jobId);
    }

    const response = await apiService.get<{
      is_success?: boolean;
      data?: Array<{ annotations?: Array<{ id?: number; grader_id?: number | string }> }>;
    }>(
      endpoint,
      Object.keys(queryParams).length > 0 ? queryParams : undefined,
      baseUrl
    );

    if (!response.success || !response.data?.is_success || !response.data.data?.length) {
      return undefined;
    }

    const rows = response.data.data[0]?.annotations;
    if (!Array.isArray(rows) || rows.length === 0) {
      return undefined;
    }

    const graderKey = String(graderId);
    const own = rows.find(ann => ann?.grader_id != null && String(ann.grader_id) === graderKey);
    const target = own ?? rows[0];
    return normalizeAnnotationId(target?.id);
  } catch {
    return undefined;
  }
}
